/* eslint-disable no-unused-vars */
/**
 * Fetch content from brooklyn
 * @module services/gift-management
 */
import get from 'lodash/get'
import isUndefined from 'lodash/isUndefined'
import isNull from 'lodash/isNull'
import { fromJS, Map } from 'immutable'
import request from '../request'
import { getLocationPathname, getLocationQuery } from '../location'
import { stringify } from '../query-string'
import { createAuthorization } from '../auth'

export const GIFT_STORE_KEY = 'gift'

export const DATE_FORMAT = 'yyyy-MM-dd'

export const DEFAULT_GIFTS_LIMIT = 20

const addPrefix = (value) => (value < 10 ? `0${value}` : value)

export const toDateString = (date) => {
  const dd = addPrefix(date.getDate())
  const MM = addPrefix(date.getMonth() + 1)
  const yyyy = date.getFullYear()

  return `${yyyy}-${MM}-${dd}`
}

export const getDate = (stringDateTime) => {
  if (!stringDateTime) {
    return ''
  }

  const [date] = stringDateTime.split('T')

  return date
}

const UPDATE_KEY = 'update'
const REDEEM_KEY = 'redeem'
const CANCEL_KEY = 'cancel'

const ALLOWED_CANCELED_FIELDS = ['cancelGift']

const ALLOWED_REDEEM_FIELDS = [
  'userInfoUuid',
  'startDate',
  'cancelGift',
  'addEntitlement',
  'skipEmarsys',
]

const ALLOWED_UPDATE_FIELDS = [
  'recipientFirstName',
  'recipientLastName',
  'recipientEmail',
  'recipientUuid',
  'deliveryDate',
  'deliveredAt',
  'gifterName',
  'message',
  'crNotes',
]

const NULLABLE_FIELDS = ['deliveryDate', 'deliveredAt']

const PREFORM_FIELDS = {
  [UPDATE_KEY]: ALLOWED_UPDATE_FIELDS,
  [REDEEM_KEY]: ALLOWED_REDEEM_FIELDS,
  [CANCEL_KEY]: ALLOWED_CANCELED_FIELDS,
}

const getValue = (fieldName, value) => {
  if (value === '' && NULLABLE_FIELDS.includes(fieldName)) {
    return null
  }

  return value
}

const preformValue = (data, key = UPDATE_KEY) => PREFORM_FIELDS[key].reduce((acc, fieldName) => {
  const value = data.get(fieldName)

  if (!isUndefined(value) && !isNull(value)) {
    acc[fieldName] = getValue(fieldName, value)
  }

  return acc
}, {})

export const createGiftListViewOptions = ({ location }) => {
  const locationQuery = getLocationQuery(location)

  return Map({
    ...locationQuery.toJS(),
    p: locationQuery.get('p') || 0,
    pp: DEFAULT_GIFTS_LIMIT,
  })
}

export const getGiftList = async ({ auth, server, ...query }) => {
  const { body } = await request
    .get(`${server}/v1/gift/search?${stringify(query)}`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))

  return fromJS({
    gifts: get(body, 'rows', []),
    total: get(body, 'count', 0),
  })
}

export const createGiftDetailViewOptions = ({ location }) => {
  const token = getLocationPathname(location).split('/')[2]

  return Map({ token })
}

export const getGift = async ({ auth, server, token }) => {
  const { body } = await request
    .get(`${server}/v1/gift?token=${token}`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))

  return fromJS({
    gifts: [body],
    total: 1,
  })
}

export const updateGift = async ({ auth, server, data }) => {
  const { body } = await request
    .put(`${server}/v1/gift/${data.get('token')}`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .send(preformValue(data))

  return fromJS({
    gifts: [body],
    total: 1,
  })
}

export const redeemGift = async ({ auth, server, data }) => {
  const isCancel = data.get('cancelGift')
  const key = isCancel ? CANCEL_KEY : REDEEM_KEY

  const { body: { gift } } = await request
    .put(`${server}/v1/gift/admin/${data.get('token')}`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .send(preformValue(data, key))

  return fromJS({
    gifts: [gift],
    total: 1,
  })
}

export const getInitialGiftValue = (gift = Map()) => ({
  // Beacause null for imuttable js is value, but form input doesn't allow have null in value
  recipientEmail: gift.get('recipientEmail', '') ?? '',
  recipientFirstName: gift.get('recipientFirstName', '') ?? '',
  recipientLastName: gift.get('recipientLastName', '') ?? '',
  deliveryDate: getDate(gift.get('deliveryDate')),
  expiresAt: getDate(gift.get('expiresAt')),
  redeemedAt: getDate(gift.get('redeemedAt')),
  message: gift.get('message', '') ?? '',
  crNotes: gift.get('crNotes', '') ?? '',
  token: gift.get('token', '') ?? '',
})

export const getInitialRedeemGiftValue = () => ({
  userInfoUuid: '',
  startDate: '',
  cancelGift: false,
  addEntitlement: true,
  skipEmarsys: false,
})

/**
 * Return true when in content list page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname is '/gift-management'
 */
export const isGiftListPage = ({ location }) => getLocationPathname(location) === '/gift-management'

/**
 * Return true when in content list page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname consist of  '/gift-management/{someToken}'
 */
export const isGiftDetailPage = ({ location }) => {
  const pathnames = getLocationPathname(location).split('/')

  return pathnames.includes('gift-management') && pathnames.length === 3
}
